import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';
import { bindActionCreators } from 'redux';

import { Link } from 'react-router-dom';
import {
    Typography, IconButton,
} from '@material-ui/core';

import urls from '../../../app/urls';
import {
    datestamp, isSameDate, getWeekStartDate, addDays, timeOnly, hasTime, isToday, today, msInOneWeek, getWeekNumberFromDate,
} from '../../../youwell-common/dateUtils';

import * as PageSettingStore from '../../../app/store/PageSettingStore';

import { PrevIcon, NextIcon, AddIcon } from '../../../common/icons';
import { sortByDateAscending } from '../../../youwell-common/arrayUtils';
import EditTask from './components/EditTask';
import { isTouchDevice } from '../../../youwell-common/browserUtils';
import AddCustomTask from './components/AddCustomTask';
import { patientProgramElementGroups } from '../../../common/constants';
import { ModalAction } from '../../../common/components';

const styles = theme => ({
    root: {
    },
    header: {
        display: 'flex',
        alignItems: 'center',
    },
    noTasksInWeek: {
        margin: theme.spacing(4, 2),
        padding: theme.spacing(1),
        background: theme.palette.primary.light,
        textAlign: 'center',
        fontSize: theme.typography.body2.fontSize,

        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            margin: theme.spacing(1, 0),
        },
    },
    weekTitle: {
        flex: '1 1 auto',
    },
    calendar: {
        padding: theme.spacing(1),
        border: theme.youwell.borders.normal,
        borderTopColor: theme.palette.primary.main,
        borderTopWidth: 4,
        backgroundColor: theme.palette.common.white,

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0),
            border: 0,
            backgroundColor: 'transparent',
        },
    },
    week: {
        display: 'flex',
        flexWrap: 'wrap',

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',

            '$noTasksInWeek + &': {
                display: 'none',
            },
        },
    },
    day: {
        flex: 1,
        padding: theme.spacing(0.5),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 150,
        overflow: 'hidden',

        [theme.breakpoints.down('xs')]: {
            padding: 0,
            minHeight: 0,
        },
    },
    outOfDate: {
        [theme.breakpoints.down('xs')]: {
            opacity: 0.5,
        },
    },
    borderLeft: {
        borderLeft: theme.youwell.borders.dim,
        [theme.breakpoints.down('xs')]: {
            borderLeft: 0,
        },
    },
    dayHeader: {
        textTransform: 'uppercase',
        padding: theme.spacing(0, 0.5),

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(0.5),
            background: theme.palette.primary.light,
            marginTop: theme.spacing(1),
            display: 'flex',
            alignItems: 'center',
        },
    },
    mobileFlex: {
        [theme.breakpoints.down('xs')]: {
            flex: '1 1 auto',
        },
    },
    mobileDate: {
        marginRight: theme.spacing(1),
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    mobileAddTask: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(4),

        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    dayHeaderDate: {
        padding: theme.spacing(0, 0.5),
        borderBottom: theme.youwell.borders.dim,
        marginBottom: theme.spacing(0.5),

        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    taskList: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    current: {
        backgroundColor: theme.palette.grey[300],
        [theme.breakpoints.down('xs')]: {
            backgroundColor: 'transparent',
        },
    },
    task: {
        position: 'relative',
        userSelect: 'none',
        maxWidth: 150,
        border: theme.youwell.borders.dim,
        padding: theme.spacing(1),
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[50],

        [theme.breakpoints.down('xs')]: {
            maxWidth: '100%',
            margin: theme.spacing(0, 0, 1),
            border: 'none',
        },
    },
    taskTopLine: {
        display: 'flex',
        justifyContent: 'flex-end',
        borderBottom: theme.youwell.borders.dim,
    },
    taskTime: {
    },
    taskName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    editIcon: {
        position: 'absolute',
        bottom: 2,
        right: 2,
        padding: 4,
        display: 'none',
        color: theme.palette.primary.main,

        '$task:hover &': {
            display: 'inline',
        },
    },
    noContent: {
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    addIcon: {
        color: 'transparent',
        alignSelf: 'center',

        '&:focus, &:hover, $day:hover &': {
            color: theme.palette.primary.main,
            transition: theme.transitions.create('color', { duration: 200 }),
        },

        '$day$touchDevice &': {
            color: theme.palette.primary.main,
            opacity: 0.7,
        },
        '$day$touchDevice:active &, $day$touchDevice:focus &, $day$touchDevice:focus-within &': {
            opacity: 1,
        },

        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    touchDevice: {
    },
});


class Calendar extends React.Component {
    state = {
        editItem: null,
    }

    setWeek = add => () => {
        const newDate = new Date(this.props.calendarDate.getTime() + (add * msInOneWeek));
        this.props.setCalendarDate(newDate);
    }

    clickTask = element => (event) => {
        if (element.createdByPatient) {
            this.setState({ editItem: element });
            event.preventDefault();
        }
    }

    closeEditElement = () => {
        this.setState({ editItem: null });
    }

    render() {
        const {
            classes, translate, currentProgram, calendarDate,
        } = this.props;

        if (!currentProgram) {
            return null;
        }

        const elements = currentProgram.groupedElements
            ? [].concat(currentProgram.groupedElements[patientProgramElementGroups.default] || [],
                currentProgram.groupedElements[patientProgramElementGroups.appointment] || [])
            : [];

        const currentWeek = getWeekNumberFromDate(calendarDate);
        const weekStartDate = getWeekStartDate(currentWeek);
        const week = [
            { key: '1', elements: [], date: weekStartDate },
            { key: '2', elements: [], date: addDays(weekStartDate, 1) },
            { key: '3', elements: [], date: addDays(weekStartDate, 2) },
            { key: '4', elements: [], date: addDays(weekStartDate, 3) },
            { key: '5', elements: [], date: addDays(weekStartDate, 4) },
            { key: '6', elements: [], date: addDays(weekStartDate, 5) },
            { key: '0', elements: [], date: addDays(weekStartDate, 6) },
        ];

        if (elements) {
            for (let i = 0; i < elements.length; i++) {
                if (elements[i].startDay > 0 && elements[i].startDay < 7) {
                    week[elements[i].startDay - 1].elements.push(elements[i]);
                } else if (elements[i].startDay === 0) {
                    week[6].elements.push(elements[i]);
                } else if (elements[i].startTime) {
                    for (let j = 0; j < week.length; j++) {
                        if (isSameDate(elements[i].startTime, week[j].date)) {
                            week[j].elements.push(elements[i]);
                        }
                    }
                }
            }
        }

        const todayDate = today();
        const noElementsInWeek = week.reduce((prev, curr) => prev + curr.elements.length, 0) < 1;

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <IconButton onClick={this.setWeek(-1)} aria-label={translate('prevWeek')}><PrevIcon /></IconButton>
                    <Typography variant="subtitle1" align="center" color="primary" className={classes.weekTitle} display="block">
                        {translate('calendar.weekNr', { weekNr: currentWeek })}
                    </Typography>
                    <IconButton onClick={this.setWeek(1)} aria-label={translate('nextWeek')}><NextIcon /></IconButton>
                </div>
                <div className={classes.calendar}>
                    {noElementsInWeek && (
                        <Typography className={classes.noTasksInWeek}>
                            {translate('calendar.noElementsInWeek')}
                        </Typography>
                    )}
                    <div className={classes.week}>
                        {week.map((day, dayIndex) => (
                            <div
                                key={day.key}
                                className={classnames(classes.day, {
                                    [classes.borderLeft]: dayIndex > 0,
                                    [classes.noContent]: day.elements.length < 1,
                                    [classes.current]: isToday(day.date),
                                    [classes.outOfDate]: day.date < todayDate,
                                    [classes.touchDevice]: isTouchDevice,
                                })}
                            >
                                <Typography variant="subtitle1" className={classes.dayHeader}>
                                    {translate(`time.weekdays.${day.key}`)}
                                    <span className={classes.mobileFlex} />
                                    <span className={classes.mobileDate}>
                                        {datestamp(day.date)}
                                    </span>
                                </Typography>
                                <Typography variant="caption" className={classes.dayHeaderDate}>
                                    {datestamp(day.date)}
                                </Typography>
                                <div className={classnames(classes.taskList)}>
                                    {sortByDateAscending(day.elements, 'startTime').map(element => (
                                        <Link
                                            key={element.id}
                                            className={classes.task}
                                            onClick={this.clickTask(element)}
                                            to={`${urls.inside.program}/${element.elementGroup}/${element.indexInGroup}`}
                                        >
                                            {hasTime(element.startTime) && (
                                                <div className={classes.taskTopLine}>
                                                    <Typography variant="caption" className={classes.taskTime}>
                                                        {timeOnly(element.startTime, translate)}
                                                    </Typography>
                                                </div>
                                            )}
                                            <Typography className={classes.taskName}>
                                                {element.name}
                                            </Typography>
                                        </Link>
                                    ))}
                                    <ModalAction
                                        className={classes.addIcon}
                                        modalHeading={translate('addTask.addTaskOrAppointment')}
                                        buttonText={translate('addTask.addTask')}
                                        Icon={AddIcon}
                                        Modal={AddCustomTask}
                                        ModalProps={{
                                            date: day.date,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={classes.mobileAddTask}>
                        <AddCustomTask
                            className={classes.mobileAddIcon}
                            date={todayDate}
                        />
                    </div>
                </div>

                {this.state.editItem && (
                    <EditTask element={this.state.editItem} onClose={this.closeEditElement} />
                )}
            </div>
        );
    }
}

Calendar.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    currentProgram: PropTypes.object,
    calendarDate: PropTypes.object.isRequired,
    setCalendarDate: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
    currentProgram: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    currentProgram: state.programStore.currentProgram,
    calendarDate: state.pageSettings.calendarDate,
});


const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PageSettingStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Calendar));
